
import {
  computed,
  defineComponent,
  onMounted,
  reactive,
  Ref,
  toRefs
} from "vue";
import Checkbox from "primevue/checkbox";
import { PaginationResult, defaultResults } from "../../pagination";
import {
  usePlanilhaTrechosService,
  TrechoPayload,
  TrechoGovPassFilterPayload
} from "../../modules/business/trecho/planilhaTrechosBusiness";
import { useUfService } from "../../modules/business/common/commonBusiness";
import { useToast } from "../../modules/message";

const LIMITE_ALERTA_TRECHOS = 500;

interface SelectorTrechosGovPassState {
  results: PaginationResult<TrechoPayload>;
  filter: TrechoGovPassFilterPayload;
  page: number;
  selecionados: Record<number, TrechoPayload>;
  alerta500Disparado: boolean;
  stateReady: boolean;
  loadings: Array<Ref<boolean>>;
}

export default defineComponent({
  setup(props, { emit }) {
    const state = reactive<SelectorTrechosGovPassState>({
      results: defaultResults(),
      filter: usePlanilhaTrechosService().safeFilterTrechoGovPass(),
      page: 0,
      selecionados: {},
      alerta500Disparado: false,
      stateReady: false,
      loadings: []
    });

    const stateLoading = computed(() => {
      return state.loadings.length !== 0;
    });

    const qtdSelecionados = computed(() => {
      return Object.keys(state.selecionados).length;
    });

    const getUfSigla = (idUnidadeFederal?: number | null) => {
      return useUfService().getUfSigla(idUnidadeFederal ?? undefined);
    };

    const executeSearch = () => {
      const {
        data,
        loading,
        searchTrechosGovPass
      } = usePlanilhaTrechosService();
      state.loadings.push(loading);
      return searchTrechosGovPass(props.idPlanilhaMae, state.filter, state.page)
        .then(() => {
          state.results = data.value;
          state.page = state.results.page.number;
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    const submitSearch = () => {
      state.page = 0;
      state.results = defaultResults();
      return executeSearch();
    };

    const clearFilter = () => {
      state.filter = usePlanilhaTrechosService().safeFilterTrechoGovPass();
      submitSearch();
    };

    const afterPageChange = (page: number) => {
      state.page = page;
      executeSearch();
    };

    const isSelecionado = (idOrigem?: number | null) => {
      return idOrigem != null && !!state.selecionados[idOrigem];
    };

    const rowClass = (trecho: TrechoPayload) => {
      return isSelecionado(trecho?.idOrigem) ? "linha-selecionada" : "";
    };

    const verificaLimite = () => {
      if (
        qtdSelecionados.value > LIMITE_ALERTA_TRECHOS &&
        !state.alerta500Disparado
      ) {
        state.alerta500Disparado = true;
        useToast().warning(
          "A seleção possui mais de 500 trechos. A chance de reprovação é alta.",
          { life: 30000 }
        );
      }
    };

    const toggleTrecho = (trecho: TrechoPayload) => {
      const idOrigem = trecho.idOrigem;
      if (idOrigem == null) {
        return;
      }
      if (state.selecionados[idOrigem]) {
        delete state.selecionados[idOrigem];
      } else {
        state.selecionados[idOrigem] = trecho;
        verificaLimite();
      }
    };

    const concluir = () => {
      emit("confirmar-selecao", Object.values(state.selecionados));
    };

    const cancelar = () => {
      emit("cancelar-selecao");
    };

    onMounted(() => {
      (props.trechosJaSelecionados ?? []).forEach((t) => {
        if (t && t.idOrigem != null) {
          state.selecionados[t.idOrigem] = t;
        }
      });
      state.stateReady = true;
      submitSearch();
    });

    return {
      stateLoading,
      qtdSelecionados,
      getUfSigla,
      executeSearch,
      submitSearch,
      clearFilter,
      afterPageChange,
      isSelecionado,
      rowClass,
      toggleTrecho,
      concluir,
      cancelar,
      ...toRefs(state)
    };
  },
  props: {
    idPlanilhaMae: {
      type: Number,
      required: true
    },
    trechosJaSelecionados: {
      type: Array as () => Array<TrechoPayload>
    }
  },
  emits: ["confirmar-selecao", "cancelar-selecao"],
  components: { Checkbox }
});
